import React, { useMemo } from "react";
import {
  Card,
  Typography,
  Box,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { MoreHoriz } from "@mui/icons-material";
import { TOPIC_LIST } from "../data/publisher.data";
import { categories } from "../data/source.data";
import SoothCard from "components/base/SoothCard";
import { formatLargeNumber, getTooltipInfo } from "../utils/util";
import BaseDataGrid from "components/base/BaseDataGrid";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";

export default function PublisherComparisonTable({
  data,
  isLoading,
  topic,
  topicValue,
  setTopic,
  category,
  setCategory,
}) {
  const rows = useMemo(() => {
    if (!Array.isArray(data)) return [];
    return data.map((item, index) => ({
      id: index + 1,
      rank: item.rank,
      publisher:
        item.publisher || `Publisher ${String.fromCharCode(65 + index)}`,
      views: item.views,
      category: item.category,
      url: item.domain || null,
    }));
  }, [data]);

  const columns = useMemo(
    () => [
      {
        field: "rank",
        headerName: "Rank",
        resizable: false,
        width: 80,
        sortable: false,
      },
      {
        field: "publisher",
        headerName: "Publisher",
        resizable: false,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const domain = params.row?.url;
        
          return (
            <Tooltip
              title={domain}
              followCursor
            >
              <Typography
                variant="inherit"
                noWrap
                sx={{
                  cursor: "default",
                  "&:hover": { color: "primary.main" },
                }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          );
        },
        
      },
      {
        field: "category",
        flex: 1,
        headerName: "Category",
        resizable: false,
        sortable: false,
        renderCell: (params) => {
          return (
            <Tooltip
              title={params.value}
              followCursor
            >
              <Typography
                variant="inherit"
                noWrap
                sx={{
                  cursor: "default",
                }}
              >
                {params.value}
              </Typography>
            </Tooltip>
          );
        },
      },
      {
        field: "views",
        headerName: "Views",
        resizable: false,
        width: 100,
        sortable: false,
        renderCell: (params) => {
          const rawValue = params.value;
          const formattedValue = formatLargeNumber(rawValue);
          const { isRounded, actualValue } = getTooltipInfo(
            rawValue,
            formattedValue,
            formatLargeNumber
          );

          const valueElement = (
            <Typography variant="inherit">{formattedValue}</Typography>
          );

          if (isRounded && actualValue) {
            return <Tooltip followCursor title={actualValue}>{valueElement}</Tooltip>;
          }

          return valueElement;
        },
      },
      // {
      //   field: 'more',
      //   headerName: 'More',
      //   width: 80,
      //   sortable: false,
      //   renderCell: () => (
      //     <IconButton size="small" sx={{ color: 'primary.dark' }}>
      //       <MoreHoriz />
      //     </IconButton>
      //   ),
      // }
    ],
    []
  );

  return (
    <SoothCard
      sx={{
        backgroundColor: (theme) => theme.palette.secondary.main,
        borderRadius: 2,
        p: 2,
        overflow: "hidden",
        ...(isLoading && { height: 500 }),
      }}
    >
             <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
         <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 2 }}>
           <Typography variant="h4" sx={{ textAlign: 'center' }}>
             Publisher Ranking
           </Typography>
           <Tooltip title="Comparison of publisher performance across different topics, showing rankings and view counts." placement="top">
             <IconButton size="small" sx={{ p: 0.5 }}>
               <InfoOutlinedIcon fontSize="small" color="primary" />
             </IconButton>
           </Tooltip>
         </Box>
         <Box sx={{ display: 'flex', gap: 2 }}>
           <FormControl sx={{ minWidth: 150, maxWidth: 200 }}>
             <InputLabel>Topic</InputLabel>
             <Select
               value={topicValue}
               label="Topic"
               onChange={(e) => setTopic(e.target.value)}
               size="small"
               sx={{ maxWidth: 200 }}
             >
               {TOPIC_LIST.map((topic) => (
                 <MenuItem key={topic.topicId} value={topic.topicId}>
                   {topic.topicName}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>
           <FormControl sx={{ minWidth: 150, maxWidth: 200 }}>
             <InputLabel id="category-select-label">Category</InputLabel>
             <Select
               labelId="category-select-label"
               value={category}
               label="Category"
               onChange={(e) => setCategory(e.target.value)}
               size="small"
               sx={{ maxWidth: 200 }}
             >
               {categories.map((cat) => (
                 <MenuItem key={cat.categoryId} value={cat.categoryId}>
                   {cat.sourceCategory}
                 </MenuItem>
               ))}
             </Select>
           </FormControl>
         </Box>
       </Box>
      <BaseDataGrid
        rows={rows}
        columns={columns}
        loading={isLoading}
        disableColumnMenu={true}
        hideFooter
        disableSelectionOnClick
        slotProps={{
          loadingOverlay: {
            variant: "skeleton",
            noRowsVariant: "skeleton",
          },
        }}
        sx={{
          border: "none",
          ".MuiDataGrid-cell": {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
          ".MuiDataGrid-columnHeaders": {
            borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
          },
        }}
      />
    </SoothCard>
  );
}
